import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ChatFileAttachment, ChatImageAttachment } from 'src/app/components/ai/chat/interfaces/chat-component.interfaces';

export type FloatingThreadDisplayMode = 'floating' | 'pinned';
export type FloatingThreadPresentation = 'floating' | 'pinned' | 'fullscreen';

export interface FloatingThreadState {
  threadId: string;
  isMinimized: boolean;
  isMaximized: boolean;
  displayMode: FloatingThreadDisplayMode;
  position: { x: number; y: number };
  size: { width: number; height: number };
  originalPosition?: { x: number; y: number }; // Store original position for maximize/restore
  originalSize?: { width: number; height: number }; // Store original size for maximize/restore
  floatingPosition?: { x: number; y: number };
  floatingSize?: { width: number; height: number };
  restoreDisplayMode?: FloatingThreadDisplayMode;
  zIndex: number;
}

export interface FloatingThreadDraft {
  message: string;
  systemPromptOverride?: string | null;
  imageAttachments?: ChatImageAttachment[];
  fileAttachments?: ChatFileAttachment[];
}

@Injectable({
  providedIn: 'root'
})
export class FloatingThreadService {
  private readonly STORAGE_KEY = 'floating-threads';
  private readonly DEFAULT_SIZE = { width: 400, height: 500 };
  private readonly DEFAULT_POSITION = { x: 20, y: 20 }; // Bottom left will be calculated
  private readonly CASCADE_OFFSET = 30;
  private readonly MOBILE_BREAKPOINT = 768; // Match with component mobile detection

  private floatingThreadsSubject = new BehaviorSubject<FloatingThreadState[]>([]);
  public floatingThreads$ = this.floatingThreadsSubject.asObservable();
  private threadDrafts = new Map<string, FloatingThreadDraft>();

  private nextZIndex = 1000;
  
  // Debouncing for storage saves during drag/resize operations
  private saveTimeout: any = null;
  private readonly SAVE_DEBOUNCE_MS = 200; // Reduced debounce time

  constructor() {
    // Only load from storage in browser environment
    if (this.isBrowser()) {
      this.loadFromStorage();
    }
  }

  /**
   * Check if we're running in browser environment
   */
  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }

  /**
   * Check if we're on a mobile device
   */
  private isMobile(): boolean {
    return this.isBrowser() && window.innerWidth <= this.MOBILE_BREAKPOINT;
  }

  /**
   * Break out multiple threads with efficient tiling layout
   */
  breakOutThreadsTiled(threadIds: string[]): void {
    if (!threadIds || threadIds.length === 0) return;
    
    // Mobile: just open them maximized (will be fullscreen)
    if (this.isMobile()) {
      threadIds.forEach(id => this.breakOutThread(id, { startMaximized: true }));
      return;
    }
    
    const existingThreads = this.floatingThreadsSubject.value;
    const newThreads: FloatingThreadState[] = [];
    
    // Filter out already opened threads
    const threadsToOpen = threadIds.filter(id => !existingThreads.some(t => t.threadId === id));
    
    if (threadsToOpen.length === 0) {
      // All threads already open, just focus them
      threadIds.forEach(id => this.focusThread(id));
      return;
    }
    
    // Calculate optimal grid layout
    const padding = 16; // Padding from edges
    const gap = 16; // Gap between panels
    const minWidth = 350; // Minimum panel width
    const minHeight = 400; // Minimum panel height
    
    const availableWidth = window.innerWidth - (padding * 2);
    const availableHeight = window.innerHeight - (padding * 2);
    
    // Calculate grid dimensions
    const count = threadsToOpen.length;
    let cols = Math.ceil(Math.sqrt(count));
    let rows = Math.ceil(count / cols);
    
    // Adjust if panels would be too small
    let panelWidth = (availableWidth - (gap * (cols - 1))) / cols;
    let panelHeight = (availableHeight - (gap * (rows - 1))) / rows;
    
    // If panels are too small, reduce columns
    while (panelWidth < minWidth && cols > 1) {
      cols--;
      rows = Math.ceil(count / cols);
      panelWidth = (availableWidth - (gap * (cols - 1))) / cols;
      panelHeight = (availableHeight - (gap * (rows - 1))) / rows;
    }
    
    // If panels are still too small height-wise, reduce rows
    while (panelHeight < minHeight && rows > 1) {
      rows--;
      cols = Math.ceil(count / rows);
      panelWidth = (availableWidth - (gap * (cols - 1))) / cols;
      panelHeight = (availableHeight - (gap * (rows - 1))) / rows;
    }
    
    // Ensure minimum sizes
    panelWidth = Math.max(minWidth, Math.min(panelWidth, 600)); // Max 600px width
    panelHeight = Math.max(minHeight, Math.min(panelHeight, 700)); // Max 700px height
    
    // Create threads with calculated positions
    threadsToOpen.forEach((threadId, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      
      const x = padding + (col * (panelWidth + gap));
      const y = padding + (row * (panelHeight + gap));
      
      newThreads.push({
        threadId,
        isMinimized: false,
        isMaximized: false,
        displayMode: 'floating',
        position: { x, y },
        size: { width: panelWidth, height: panelHeight },
        zIndex: this.nextZIndex++
      });
    });
    
    // Update all threads at once
    const updatedThreads = [...existingThreads, ...newThreads];
    this.updateThreads(updatedThreads);
    
    // Focus any threads that were already open
    const alreadyOpenThreads = threadIds.filter(id => existingThreads.some(t => t.threadId === id));
    alreadyOpenThreads.forEach(id => this.focusThread(id));
  }

  /**
   * Break out a thread into a floating panel
   */
  breakOutThread(
    threadId: string, 
    options?: {
      initialSize?: { width: number; height: number };
      initialPosition?: { x: number; y: number };
      startMaximized?: boolean;
    }
  ): void {
    // On mobile, always force fullscreen mode
    const forceMaximized = this.isMobile() || options?.startMaximized;

    this.showThread(threadId, forceMaximized ? 'fullscreen' : 'floating', options);
  }

  showThread(
    threadId: string,
    presentation: FloatingThreadPresentation,
    options?: {
      initialSize?: { width: number; height: number };
      initialPosition?: { x: number; y: number };
    }
  ): void {
    const existingThreads = this.floatingThreadsSubject.value;
    const existingThread = existingThreads.find(thread => thread.threadId === threadId);

    if (!existingThread) {
      const newThread = this.createThreadForPresentation(threadId, presentation, existingThreads.length, options);
      this.updateThreads([...existingThreads, newThread]);
      return;
    }

    const updatedThreads = existingThreads.map((currentThread, index) => {
      if (currentThread.threadId === threadId) {
        return this.convertThreadToPresentation(currentThread, presentation, index);
      }

      if (presentation === 'pinned' && currentThread.displayMode === 'pinned') {
        return this.convertThreadToFloating(currentThread, index);
      }

      return currentThread;
    });

    this.updateThreads(updatedThreads);
  }

  setThreadDraft(
    threadId: string,
    draft: string,
    systemPromptOverride: string | null = null,
    imageAttachments: ChatImageAttachment[] = [],
    fileAttachments: ChatFileAttachment[] = []
  ): void {
    if (!threadId) {
      return;
    }

    const trimmedDraft = draft?.trim() || '';
    if (!trimmedDraft && imageAttachments.length === 0 && fileAttachments.length === 0) {
      this.threadDrafts.delete(threadId);
      return;
    }

    this.threadDrafts.set(threadId, {
      message: trimmedDraft,
      systemPromptOverride,
      ...(imageAttachments.length > 0 ? { imageAttachments } : {}),
      ...(fileAttachments.length > 0 ? { fileAttachments } : {})
    });
  }

  consumeThreadDraft(threadId: string): FloatingThreadDraft | null {
    if (!threadId || !this.threadDrafts.has(threadId)) {
      return null;
    }

    const draft = this.threadDrafts.get(threadId) || null;
    this.threadDrafts.delete(threadId);
    return draft;
  }

  /**
   * Remove a thread from floating panels (close it)
   */
  closeThread(threadId: string): void {
    const existingThreads = this.floatingThreadsSubject.value;
    const updatedThreads = existingThreads.filter(t => t.threadId !== threadId);
    this.updateThreads(updatedThreads);
  }

  /**
   * Toggle minimize state of a thread
   * On mobile, unminimizing automatically maximizes to fullscreen
   */
  toggleMinimize(threadId: string): void {
    const existingThreads = this.floatingThreadsSubject.value;
    const thread = existingThreads.find(t => t.threadId === threadId);
    
    if (!thread) return;

    const restoredDisplayMode = thread.restoreDisplayMode || thread.displayMode;
    const restoredPosition = thread.originalPosition || thread.floatingPosition || thread.position;
    const restoredSize = thread.originalSize || thread.floatingSize || thread.size;
    
    // If currently minimized and we're on mobile, maximize to fullscreen when unminimizing
    if (thread.isMinimized && this.isMobile()) {
      const maximizedSize = this.getMaximizedSize();
      const maximizedPosition = this.getMaximizedPosition(maximizedSize);
      
      const updatedThreads = existingThreads.map<FloatingThreadState>(t => 
        t.threadId === threadId 
          ? { 
              ...t, 
              isMinimized: false,
              isMaximized: true,
              displayMode: 'floating',
              restoreDisplayMode: t.displayMode,
              originalPosition: restoredPosition,
              originalSize: restoredSize,
              position: maximizedPosition,
              size: maximizedSize,
              zIndex: this.nextZIndex++
            }
          : t
      );
      this.updateThreads(updatedThreads);
    } else {
      // Desktop behavior: just toggle minimize state
      const updatedThreads = existingThreads.map<FloatingThreadState>(t => 
        t.threadId === threadId 
          ? { 
              ...t, 
              isMinimized: !t.isMinimized,
              isMaximized: t.isMinimized ? t.isMaximized : false,
              displayMode: t.isMinimized ? t.displayMode : (t.displayMode === 'pinned' ? 'floating' : restoredDisplayMode),
              position: t.isMinimized ? t.position : restoredPosition,
              size: t.isMinimized ? t.size : restoredSize,
              floatingPosition: t.isMinimized ? t.floatingPosition : (t.floatingPosition || restoredPosition),
              floatingSize: t.isMinimized ? t.floatingSize : (t.floatingSize || restoredSize),
              originalPosition: t.isMinimized ? t.originalPosition : undefined,
              originalSize: t.isMinimized ? t.originalSize : undefined,
              restoreDisplayMode: t.isMinimized ? t.restoreDisplayMode : undefined
            }
          : t
      );
      this.updateThreads(updatedThreads);
    }
  }

  /**
   * Toggle maximize state of a thread
   */
  toggleMaximize(threadId: string): void {
    const existingThreads = this.floatingThreadsSubject.value;
    const thread = existingThreads.find(t => t.threadId === threadId);
    
    if (!thread) return;

    let updatedThread: FloatingThreadState;

    if (thread.isMaximized) {
      const restoredDisplayMode = thread.restoreDisplayMode || 'floating';
      const restoredPosition = thread.originalPosition || thread.floatingPosition || thread.position;
      const restoredSize = thread.originalSize || thread.floatingSize || thread.size;

      // Restore to original position and size
      updatedThread = {
        ...thread,
        isMaximized: false,
        displayMode: restoredDisplayMode,
        position: restoredPosition,
        size: restoredSize,
        floatingPosition: restoredPosition,
        floatingSize: restoredSize,
        originalPosition: undefined,
        originalSize: undefined,
        restoreDisplayMode: undefined,
        zIndex: this.nextZIndex++
      };
    } else {
      // Maximize: store current position/size and set to maximized state
      const maximizedSize = this.getMaximizedSize();
      const maximizedPosition = this.getMaximizedPosition(maximizedSize);
      const floatingPosition = thread.displayMode === 'pinned'
        ? (thread.floatingPosition || thread.position)
        : thread.position;
      const floatingSize = thread.displayMode === 'pinned'
        ? (thread.floatingSize || thread.size)
        : thread.size;
      
      updatedThread = {
        ...thread,
        isMaximized: true,
        isMinimized: false, // Ensure it's not minimized when maximized
        displayMode: 'floating',
        restoreDisplayMode: thread.displayMode,
        originalPosition: floatingPosition,
        originalSize: floatingSize,
        floatingPosition,
        floatingSize,
        position: maximizedPosition,
        size: maximizedSize,
        zIndex: this.nextZIndex++
      };
    }

    const updatedThreads = existingThreads.map(t => 
      t.threadId === threadId ? updatedThread : t
    );
    this.updateThreads(updatedThreads);
  }

  /**
   * Bring a thread to front and expand if minimized
   * On mobile, also ensure thread is maximized for fullscreen experience
   */
  focusThread(threadId: string): void {
    const existingThreads = this.floatingThreadsSubject.value;
    const thread = existingThreads.find(t => t.threadId === threadId);
    
    if (!thread) return;
    
    // On mobile, always maximize when focusing (for fullscreen)
    if (this.isMobile() && !thread.isMaximized) {
      const maximizedSize = this.getMaximizedSize();
      const maximizedPosition = this.getMaximizedPosition(maximizedSize);
      const restoredPosition = thread.floatingPosition || thread.position;
      const restoredSize = thread.floatingSize || thread.size;
      
      const updatedThreads = existingThreads.map<FloatingThreadState>(t => 
        t.threadId === threadId 
          ? { 
              ...t, 
              isMinimized: false,
              isMaximized: true,
              displayMode: 'floating',
              restoreDisplayMode: t.displayMode,
              originalPosition: restoredPosition,
              originalSize: restoredSize,
              position: maximizedPosition,
              size: maximizedSize,
              zIndex: this.nextZIndex++
            }
          : t
      );
      this.updateThreads(updatedThreads);
    } else {
      // Desktop: just expand if minimized and bring to front
      const updatedThreads = existingThreads.map(t => 
        t.threadId === threadId 
          ? { ...t, isMinimized: false, zIndex: this.nextZIndex++ }
          : t
      );
      this.updateThreads(updatedThreads);
    }
  }

  /**
   * Update thread position (optimized for drag operations)
   */
  updateThreadPosition(threadId: string, position: { x: number; y: number }): void {
    const existingThreads = this.floatingThreadsSubject.value;
    const updatedThreads = existingThreads.map(thread => 
      thread.threadId === threadId 
        ? { ...thread, position }
        : thread
    );
    // Update immediately, debounce storage save for drag operations
    this.floatingThreadsSubject.next(updatedThreads);
    this.debouncedSaveToStorage(updatedThreads);
  }

  /**
   * Update thread size (optimized for resize operations)
   */
  updateThreadSize(threadId: string, size: { width: number; height: number }): void {
    const existingThreads = this.floatingThreadsSubject.value;
    const updatedThreads = existingThreads.map(thread => 
      thread.threadId === threadId 
        ? { ...thread, size }
        : thread
    );
    // Update immediately, debounce storage save for resize operations
    this.floatingThreadsSubject.next(updatedThreads);
    this.debouncedSaveToStorage(updatedThreads);
  }

  /**
   * Update thread position and size simultaneously 
   */
  updateThreadPositionAndSize(
    threadId: string, 
    position: { x: number; y: number }, 
    size: { width: number; height: number }
  ): void {
    const existingThreads = this.floatingThreadsSubject.value;
    const updatedThreads = existingThreads.map(thread => 
      thread.threadId === threadId 
        ? { ...thread, position, size }
        : thread
    );
    // Update immediately, debounce storage save
    this.floatingThreadsSubject.next(updatedThreads);
    this.debouncedSaveToStorage(updatedThreads);
  }

  togglePin(threadId: string): void {
    if (this.isMobile()) {
      return;
    }

    const existingThreads = this.floatingThreadsSubject.value;
    const thread = existingThreads.find(t => t.threadId === threadId);

    if (!thread) return;

    this.showThread(threadId, thread.displayMode === 'pinned' ? 'floating' : 'pinned');
  }

  /**
   * Get all floating threads
   */
  getFloatingThreads(): FloatingThreadState[] {
    return this.floatingThreadsSubject.value;
  }

  /**
   * Clear all floating threads and reset to default state
   */
  clearAllThreads(): void {
    if (this.isBrowser()) {
      try {
        localStorage.removeItem(this.STORAGE_KEY);
        this.floatingThreadsSubject.next([]);
        this.nextZIndex = 1000;
        console.log('Floating threads cleared from localStorage');
      } catch (error) {
        console.warn('Failed to clear floating threads from localStorage:', error);
      }
    }
  }

  /**
   * Calculate position for new floating panel (bottom-center with cascade)
   */
  private calculateNewPosition(existingCount: number): { x: number; y: number } {
    const cascadeOffset = existingCount * this.CASCADE_OFFSET;
    const centerX = (window.innerWidth - this.DEFAULT_SIZE.width) / 2;
    
    return {
      x: centerX + cascadeOffset,
      y: window.innerHeight - this.DEFAULT_SIZE.height - 8 - cascadeOffset
    };
  }

  /**
   * Calculate maximized size (80% of viewport with some padding, fullscreen on mobile)
   */
  private getMaximizedSize(): { width: number; height: number } {
    // On mobile, use full viewport dimensions for true fullscreen
    if (this.isMobile()) {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      };
    }
    
    // On desktop, use constrained dimensions with padding
    const padding = 40; // 20px padding on each side
    return {
      width: Math.min(1200, window.innerWidth - padding), // Max 1200px width
      height: Math.min(800, window.innerHeight - padding)  // Max 800px height
    };
  }

  /**
   * Calculate maximized position (centered in viewport, or 0,0 on mobile for fullscreen)
   */
  private getMaximizedPosition(size: { width: number; height: number }): { x: number; y: number } {
    // On mobile, position at 0,0 for true fullscreen
    if (this.isMobile()) {
      return { x: 0, y: 0 };
    }
    
    // On desktop, center in viewport
    return {
      x: (window.innerWidth - size.width) / 2,
      y: (window.innerHeight - size.height) / 2
    };
  }

  /**
   * Update threads and save to storage
   */
  private updateThreads(threads: FloatingThreadState[]): void {
    this.floatingThreadsSubject.next(threads);
    this.debouncedSaveToStorage(threads);
  }

  private createThreadForPresentation(
    threadId: string,
    presentation: FloatingThreadPresentation,
    existingCount: number,
    options?: {
      initialSize?: { width: number; height: number };
      initialPosition?: { x: number; y: number };
    }
  ): FloatingThreadState {
    const size = options?.initialSize || { ...this.DEFAULT_SIZE };
    const position = options?.initialPosition || this.calculateNewPosition(existingCount);

    const baseThread: FloatingThreadState = {
      threadId,
      isMinimized: false,
      isMaximized: false,
      displayMode: 'floating',
      position,
      size,
      floatingPosition: position,
      floatingSize: size,
      zIndex: this.nextZIndex++
    };

    return this.convertThreadToPresentation(baseThread, presentation, existingCount);
  }

  private convertThreadToPresentation(
    thread: FloatingThreadState,
    presentation: FloatingThreadPresentation,
    existingCount: number
  ): FloatingThreadState {
    switch (presentation) {
      case 'pinned':
        return this.convertThreadToPinned(thread);
      case 'fullscreen':
        return this.convertThreadToFullscreen(thread);
      case 'floating':
      default:
        return this.convertThreadToFloating(thread, existingCount);
    }
  }

  private convertThreadToFloating(thread: FloatingThreadState, existingCount: number): FloatingThreadState {
    const position = thread.floatingPosition || thread.originalPosition || thread.position || this.calculateNewPosition(existingCount);
    const size = thread.floatingSize || thread.originalSize || thread.size || { ...this.DEFAULT_SIZE };

    return {
      ...thread,
      displayMode: 'floating',
      isMinimized: false,
      isMaximized: false,
      position,
      size,
      floatingPosition: position,
      floatingSize: size,
      originalPosition: undefined,
      originalSize: undefined,
      restoreDisplayMode: undefined,
      zIndex: this.nextZIndex++
    };
  }

  private convertThreadToFullscreen(thread: FloatingThreadState): FloatingThreadState {
    const maximizedSize = this.getMaximizedSize();
    const maximizedPosition = this.getMaximizedPosition(maximizedSize);
    const floatingPosition = thread.displayMode === 'pinned'
      ? (thread.floatingPosition || thread.position)
      : (thread.floatingPosition || thread.originalPosition || thread.position);
    const floatingSize = thread.displayMode === 'pinned'
      ? (thread.floatingSize || thread.size)
      : (thread.floatingSize || thread.originalSize || thread.size);

    return {
      ...thread,
      isMinimized: false,
      isMaximized: true,
      displayMode: 'floating',
      restoreDisplayMode: thread.displayMode,
      originalPosition: floatingPosition,
      originalSize: floatingSize,
      floatingPosition,
      floatingSize,
      position: maximizedPosition,
      size: maximizedSize,
      zIndex: this.nextZIndex++
    };
  }

  private convertThreadToPinned(thread: FloatingThreadState): FloatingThreadState {
    const floatingPosition = thread.isMaximized
      ? (thread.originalPosition || thread.floatingPosition || this.calculateNewPosition(0))
      : (thread.floatingPosition || thread.position);
    const floatingSize = thread.isMaximized
      ? (thread.originalSize || thread.floatingSize || { ...this.DEFAULT_SIZE })
      : (thread.floatingSize || thread.size);

    return {
      ...thread,
      displayMode: 'pinned',
      isMinimized: false,
      isMaximized: false,
      floatingPosition,
      floatingSize,
      position: floatingPosition,
      size: floatingSize,
      originalPosition: undefined,
      originalSize: undefined,
      restoreDisplayMode: undefined,
      zIndex: this.nextZIndex++
    };
  }

  /**
   * Debounced save to storage to avoid excessive localStorage writes during drag/resize
   */
  private debouncedSaveToStorage(threads: FloatingThreadState[]): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    this.saveTimeout = setTimeout(() => {
      this.saveToStorage(threads);
      this.saveTimeout = null;
    }, this.SAVE_DEBOUNCE_MS);
  }

  /**
   * Save floating threads to localStorage
   */
  private saveToStorage(threads: FloatingThreadState[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(threads));
    } catch (error) {
      console.warn('Failed to save floating threads to localStorage:', error);
    }
  }

  /**
   * Load floating threads from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const threads: FloatingThreadState[] = JSON.parse(stored).map((thread: Partial<FloatingThreadState>, index: number) => ({
          threadId: thread.threadId || `thread-${index}`,
          isMinimized: !!thread.isMinimized,
          isMaximized: !!thread.isMaximized,
          displayMode: thread.displayMode || 'floating',
          position: thread.position || this.calculateNewPosition(index),
          size: thread.size || { ...this.DEFAULT_SIZE },
          originalPosition: thread.originalPosition,
          originalSize: thread.originalSize,
          floatingPosition: thread.floatingPosition || thread.position,
          floatingSize: thread.floatingSize || thread.size || { ...this.DEFAULT_SIZE },
          restoreDisplayMode: thread.restoreDisplayMode,
          zIndex: thread.zIndex || this.nextZIndex++
        }));
        this.floatingThreadsSubject.next(threads);
        
        // Update nextZIndex to be higher than any existing zIndex
        const maxZIndex = Math.max(...threads.map(t => t.zIndex), 999);
        this.nextZIndex = maxZIndex + 1;
      }
    } catch (error) {
      console.warn('Failed to load floating threads from localStorage:', error);
    }
  }
}
